
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    /*
| Developed by Dirupt
| Filename : reset-password.tsx
| Author : Philippe DESPLATS (philippe@dirupt.com)
*/
import { Box, FormHelperText, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import * as Yup from 'yup';
import { Form, Formik, FormikHelpers } from 'formik';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { m } from 'framer-motion';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';
import { variantFade } from '@/components/animate';
import { AuthEndpoint } from '@/services/api/modules/auth/auth.endpoint';
import { AuthResetPasswordDto } from '@/services/api/modules/auth/auth.dto';
import { createAuthServerSideProps } from '@/components/ssr/createServerSideProps';
import { SensklePageFactory } from '@/components/ssr/SensklePage';
import { PasswordField, YupPasswordValidation } from '@/components/formik';
import { MButton } from '@/components/common/MButton';
import { Link } from '@/components/common/MLink';
import { UrlsConfig } from '@/config/urls.config';
import { MDarkModeSwitch } from '@/components/common/MDarkModeSwitch';
import { LangSwitch } from '@/components/common/LangSwitch';
/*
|--------------------------------------------------------------------------
| Constants
|--------------------------------------------------------------------------
*/
const initialValues: Pick<AuthResetPasswordDto, 'password'> & {
    confirmation: string;
} = {
    password: '',
    confirmation: '',
};
/*
|--------------------------------------------------------------------------
| Page
|--------------------------------------------------------------------------
*/
const ResetPassword = SensklePageFactory(() => {
    const { t } = useTranslation();
    const router = useRouter();
    const params = useSearchParams();
    const token = params.get('token');
    // Form
    // ----------------------------------------------------------------------------
    const validationSchema = React.useMemo(() => {
        return Yup.object().shape({
            password: YupPasswordValidation(t),
            confirmation: YupPasswordValidation(t).oneOf([Yup.ref('password')], t('common:yup.password.confirmation')),
        });
    }, [t]);
    // Methods
    // ----------------------------------------------------------------------------
    const handleSubmit = async (values: Pick<AuthResetPasswordDto, 'password'>, formikHelpers: FormikHelpers<Pick<AuthResetPasswordDto, 'password'>>) => {
        if (!token)
            return;
        try {
            await AuthEndpoint.resetPassword({
                password: values.password,
                token: (params.get('token') as string) ?? '',
            });
            toast.success(t('auth/reset-password:successes.passwordReset'));
            await router.push(UrlsConfig.feed);
        }
        catch (error) {
            if (error instanceof AxiosError) {
                switch (error?.response?.status) {
                    case 400:
                    case 403:
                        toast.error(t('auth/reset-password:errors.invalidToken'), {
                            description: t('auth/reset-password:errors.invalidTokenDescription'),
                        });
                        break;
                    case 429:
                        toast.error(t('common:errors.rateLimit'));
                        break;
                    default:
                        toast.error(t('common:errors.errorOccurred'));
                }
            }
            else {
                toast.error(t('common:errors.errorOccurred'));
            }
            console.log(error);
        }
        finally {
            formikHelpers.setSubmitting(false);
        }
    };
    // Render
    // ----------------------------------------------------------------------------
    return (<Box display={'flex'} flexDirection={'column'} justifyContent={'center'} gap={4} width={'100%'}>
			<Box>
				<Box textAlign={'center'} component={m.div} variants={variantFade().inLeft}>
					<Typography variant={'h1'}>{t('auth/reset-password:title')}</Typography>
					<Typography>
						{t(token ? 'auth/reset-password:subTitle' : 'auth/reset-password:errors.invalidToken')}
					</Typography>
				</Box>
			</Box>
			{!token ? (<Box component={m.div} variants={variantFade().inLeft} marginTop={2} marginBottom={2}>
					<Typography textAlign={'center'}>
						{t('auth/reset-password:errors.invalidTokenDescription')}
					</Typography>
				</Box>) : (<Formik<Pick<AuthResetPasswordDto, 'password'>> initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
					{(formikProps) => (<Form>
							<Box display={'flex'} flexDirection={'column'} gap={2} component={m.div} variants={variantFade().inLeft}>
								<Box>
									<PasswordField />
									<FormHelperText sx={{ marginLeft: '14px' }}>
										{t('common:form.password.policy')}
									</FormHelperText>
								</Box>
								<PasswordField name={'confirmation'} label={t('common:form.passwordConfirmation.label')}/>
								<Box display={'flex'} flexDirection={'column'} gap={1} component={m.div} variants={variantFade().inLeft}>
									<MButton variant="contained" type="submit" size={'large'} disabled={formikProps.isSubmitting}>
										{t('auth/reset-password:submitButton')}
									</MButton>
								</Box>
							</Box>
						</Form>)}
				</Formik>)}
			<Box display={'flex'} flexDirection={'column'} gap={2}>
				<Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} gap={0.5} component={m.div} variants={variantFade().inLeft}>
					{token ? (<React.Fragment>
							<Typography variant={'body2'}>{t('auth/reset-password:doYouRemember')}</Typography>
							<MButton variant={'text'} component={Link} href={UrlsConfig.auth.login}>
								{t('auth/reset-password:login')}
							</MButton>
						</React.Fragment>) : (<MButton variant={'text'} component={Link} href={UrlsConfig.auth.forgotPassword}>
							{t('auth/reset-password:forgotPassword')}
						</MButton>)}
				</Box>
				<Box display={'flex'} justifyContent={'space-between'} gap={2} component={m.div} variants={variantFade().inLeft}>
					<MDarkModeSwitch />
					<LangSwitch />
				</Box>
			</Box>
		</Box>);
});
/*
|--------------------------------------------------------------------------
| Configurations
|--------------------------------------------------------------------------
*/
ResetPassword.layout = 'auth';
export default ResetPassword;
/*
|--------------------------------------------------------------------------
| Server Side Props
|--------------------------------------------------------------------------
*/
const getServerSideProps = createAuthServerSideProps();

    async function __Next_Translate__getServerSideProps__1a09ebd47ed__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/auth/reset-password',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a09ebd47ed__ as getServerSideProps }
  